import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

/**
 * The typed section map for a scene — five sections, all hand-written, same reason as
 * the case and campaign maps: nothing references them from an operation, so OpenAPI
 * cannot emit them. Transcribed from
 * `CampaignManager.Api/Contracts/Scenes/SectionDtos.cs`.
 *
 * The enums (`TimeOfDay`, `SceneCharacterType`, `CharacterType`) *are* generated —
 * they appear on request bodies — and stay that way.
 */
export interface SceneSections {
  core: SceneCoreSection
  aspects: SceneAspectsSection
  characters: SceneCharactersSection
  panels: ScenePanelsSection
  snapshot: SceneSnapshotSection
}

/** Scene identity, its place in the case, and where and when it happens. Key `core`. */
export interface SceneCoreSection {
  id: number
  caseId: number
  caseName: string | null
  campaignId: number
  title: string
  timeOfDay: Schema['TimeOfDay']
  order: number
  locationId: number | null
  locationName: string | null
  summary: string
  hasSnapshot: boolean
  createdAt: string
}

/**
 * A situation aspect in play.
 *
 * Three fields, and that is the whole story — no free-invoke count, no hostile flag.
 * The design spec asks for both; the API has neither, so they live in
 * `sessionState.ts` and are device-local. Do not add them here.
 */
export interface SceneAspect {
  id: number
  description: string
  order: number
}

/** The scene's aspects, in order. Section key `aspects`. */
export interface SceneAspectsSection {
  aspects: SceneAspect[]
}

/** A character present in the scene, portrait already resolved to a URL. */
export interface SceneCharacter {
  /** The join row's id — what a role change or removal addresses. */
  id: number
  characterId: number
  name: string
  type: Schema['CharacterType']
  role: Schema['SceneCharacterType']
  portraitUrl: string | null
}

/**
 * The scene's cast. Section key `characters`.
 *
 * **The PC/NPC split arrives pre-computed and the client must use it.** `Scene.PCCharacters`
 * and `NPCCharacters` are server-side conveniences, and shipping the split is deliberate so
 * that no client filters on a role enum it had to learn the meaning of. `all` is the flat
 * list for anything that wants everybody in one pass.
 */
export interface SceneCharactersSection {
  all: SceneCharacter[]
  pcs: SceneCharacter[]
  npcs: SceneCharacter[]
}

/** A comic page under a panel. Read-only here; generation is Stage 8. */
export interface ScenePage {
  id: number
  panelId: number
  order: number
  directions: string
  imageId: number | null
  hasImage: boolean
  imageUrl: string | null
  imageUrlSmall: string | null
  imageUrlThumb: string | null
  /** Bumped by the storage layer on each regeneration, so a client can cache-bust. */
  imageVersion: number | null
  createdAt: string
}

/** One panel of the scene with its pages, both in order. */
export interface ScenePanel {
  id: number
  description: string
  order: number
  pages: ScenePage[]
}

/** The scene's panels, in order. Section key `panels`. */
export interface ScenePanelsSection {
  panels: ScenePanel[]
}

/**
 * The stored character-state snapshot — the rundown comic-page generation reads.
 * Section key `snapshot`, served only by `GET /scenes/{id}/snapshot`.
 */
export interface SceneSnapshotSection {
  /** Null when none has been recorded. */
  text: string | null
  /**
   * How many of the cast a regenerated snapshot would cover. Compared against the cast
   * this is the server's own staleness signal — which is why it is not derived here.
   */
  characterCount: number
}

export interface SceneSheet {
  sceneId: number
  caseId: number
  campaignId: number
  version: number
  /** Whether this caller may write. Members read; the Game Master writes. */
  isGameMaster: boolean
  sections: Partial<SceneSections>
}

export function toSceneSheet(dto: Schema['SceneSheetDto']): SceneSheet {
  if (dto.sceneId === undefined) {
    throw new Error('The API returned a scene sheet with no id.')
  }

  return {
    sceneId: dto.sceneId,
    caseId: dto.caseId ?? 0,
    campaignId: dto.campaignId ?? 0,
    version: dto.version ?? 0,
    isGameMaster: dto.isGameMaster ?? false,
    sections: (dto.sections ?? {}) as Partial<SceneSections>,
  }
}

// ---------------------------------------------------------------------------
// Presentation helpers — layout only.
// ---------------------------------------------------------------------------

/**
 * The times of day, in the order a day runs.
 *
 * Generated, so this is the one enum in the stage that cannot drift silently. The order
 * matters only for the picker; the member names read well enough as labels that a
 * translation table would be six identity entries, so there is none — the character
 * domain's `labels.ts` exists because `ChestDeep` does not read as prose, and these do.
 */
export const TIME_OF_DAY_ORDER: Array<Schema['TimeOfDay']> = [
  'Sunrise',
  'Morning',
  'Midday',
  'Sunset',
  'Evening',
  'Night',
]

export const timeOfDayLabel = (value: string): string => value

/** The scene header's second line — "MIDNIGHT · MCANALLY'S", or just the time. */
export function sceneWhereAndWhen(core: SceneCoreSection | undefined): string | undefined {
  if (core === undefined) return undefined

  const parts = [timeOfDayLabel(String(core.timeOfDay))]
  if (core.locationName !== null && core.locationName !== '') parts.push(core.locationName)

  return parts.join(' · ')
}

/**
 * Orders the cast for the turn-order card: whoever the table has arranged first, then
 * everybody else.
 *
 * The arrangement is device-local (`sessionState.ts`), so this is the one place the two
 * halves meet — a server-provided cast read through a client-local ordering. Characters
 * in the stored order who have since left the scene are dropped rather than rendered as
 * gaps.
 */
export function orderedCast(
  cast: readonly SceneCharacter[],
  turnOrder: readonly number[],
): SceneCharacter[] {
  if (turnOrder.length === 0) return [...cast]

  const byCharacterId = new Map(cast.map((entry) => [entry.characterId, entry]))
  const arranged = turnOrder
    .map((characterId) => byCharacterId.get(characterId))
    .filter((entry): entry is SceneCharacter => entry !== undefined)

  const seen = new Set(arranged.map((entry) => entry.characterId))
  return [...arranged, ...cast.filter((entry) => !seen.has(entry.characterId))]
}
